import React, { Fragment, useContext, useState, useLayoutEffect } from "react";
import { Menu, X, ChevronsLeft, ChevronsRight, Search } from "react-feather";
import HeaderContain from "./HeaderContain";
import SearchBar from "./SearchBar";
import logoDark from "../../assets/images/logo/dark-based-logo-long.svg";
import logoLight from "../../assets/images/logo/white-based-logo-long.svg";
import { SidebarContext } from "../Layout";

const MOBILE_BREAKPOINT = 992;
const TABLET_BREAKPOINT = 768;
const SMALL_MOBILE = 480;

const Header = () => {
  const { collapsed, toggleCollapsed, mobileOpen, setMobileOpen } = useContext(SidebarContext);
  const [width, setWidth] = useState(window.innerWidth);
  const [isDark, setIsDark] = useState(document.documentElement.classList.contains("dark"));
  const [showMobileSearch, setShowMobileSearch] = useState(false);

  useLayoutEffect(() => {
    const update = () => setWidth(window.innerWidth);
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  useLayoutEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDark(document.documentElement.classList.contains("dark"));
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    return () => observer.disconnect();
  }, []);

  const isMobile = width < MOBILE_BREAKPOINT;
  const isTablet = width < TABLET_BREAKPOINT;
  const isSmallMobile = width < SMALL_MOBILE;

  const iconBtnStyle = {
    background: "var(--icon-btn-bg)",
    border: "1px solid var(--icon-btn-border)",
    borderRadius: 10,
    width: isSmallMobile ? 32 : 36,
    height: isSmallMobile ? 32 : 36,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    color: "var(--text-primary)",
    transition: "all 0.18s",
    flexShrink: 0,
  };

  return (
    <Fragment>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          width: "100%",
          height: "100%",
          padding: isSmallMobile ? "0 8px" : "0 12px",
          gap: isSmallMobile ? 6 : 8,
          overflow: "hidden",
        }}
      >
        {/* Toggle button */}
        <button
          onClick={isMobile ? () => setMobileOpen((o) => !o) : toggleCollapsed}
          aria-label={
            isMobile
              ? mobileOpen
                ? "Close sidebar"
                : "Open sidebar"
              : collapsed
                ? "Expand sidebar"
                : "Collapse sidebar"
          }
          style={iconBtnStyle}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "var(--nav-hover)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "var(--icon-btn-bg)";
          }}
        >
          {isMobile ? (
            mobileOpen ? (
              <X size={isSmallMobile ? 16 : 18} />
            ) : (
              <Menu size={isSmallMobile ? 16 : 18} />
            )
          ) : collapsed ? (
            <ChevronsRight size={17} />
          ) : (
            <ChevronsLeft size={17} />
          )}
        </button>

        {/* Logo - Hide on very small screens */}
        {!isSmallMobile && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              textDecoration: "none",
              flexShrink: 0,
            }}
          >
            <img
              src={isDark ? logoDark : logoLight}
              alt="HR Fusions"
              style={{
                height: isTablet ? "24px" : isMobile ? "28px" : "36px",
                width: "auto",
                objectFit: "contain",
                maxWidth: isTablet ? "120px" : "none",
              }}
            />
          </div>
        )}

        {/* Desktop Search - Hide on tablets and below */}
        {!isTablet && (
          <div
            style={{
              flex: 1,
              maxWidth: 360,
              minWidth: 0,
            }}
          >
            <SearchBar />
          </div>
        )}

        {/* Mobile Search Button - Show on tablets and below */}
        {isTablet && (
          <button
            onClick={() => setShowMobileSearch(true)}
            aria-label="Search"
            style={iconBtnStyle}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "var(--nav-hover)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "var(--icon-btn-bg)";
            }}
          >
            <Search size={isSmallMobile ? 16 : 18} />
          </button>
        )}

        {/* Right side - Always visible but compact on mobile */}
        <div
          style={{
            marginLeft: "auto",
            display: "flex",
            alignItems: "center",
            minWidth: 0,
            flexShrink: 0,
          }}
        >
          <HeaderContain isMobile={isMobile} isTablet={isTablet} isSmallMobile={isSmallMobile} />
        </div>
      </div>

      {/* Mobile Search Overlay */}
      {showMobileSearch && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "var(--navbar-bg)",
            backdropFilter: "blur(20px)",
            zIndex: 10000,
            display: "flex",
            flexDirection: "column",
            padding: "16px",
            animation: "slideDown 0.2s ease",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
            <div style={{ flex: 1 }}>
              <SearchBar autoFocus />
            </div>
            <button
              onClick={() => setShowMobileSearch(false)}
              aria-label="Close search"
              style={{
                ...iconBtnStyle,
                background: "var(--icon-btn-bg)",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "var(--nav-hover)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "var(--icon-btn-bg)";
              }}
            >
              <X size={18} />
            </button>
          </div>
          <div
            style={{
              fontSize: "0.75rem",
              color: "var(--text-muted)",
              textAlign: "center",
            }}
          >
            Search across the application
          </div>
        </div>
      )}
    </Fragment>
  );
};

export default Header;
